import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';

// ── Async thunk: extract prescription from uploaded image/PDF ─────────────────
export const extractPrescriptionFromImage = createAsyncThunk(
  'prescription/extractFromImage',
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('prescription', file);
      const { data } = await axiosInstance.post('/prescription/extract', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.prescription;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Failed to extract prescription'
      );
    }
  }
);

const initialEye = { sph: null, cyl: null, axis: null, effectiveSph: null };

const prescriptionSlice = createSlice({
  name: 'prescription',
  initialState: {
    rightEye:       { ...initialEye },
    leftEye:        { ...initialEye },
    method:         null,       // 'manual' | 'upload'
    condition:      'none',     // famous condition if any
    addPower:       null,       // ADD for presbyopia
    timestamp:      null,
    isSet:          false,
    extractLoading: false,
    extractError:   null,
  },
  reducers: {
    setPrescription(state, action) {
      const { rightEye, leftEye, method, condition, addPower } = action.payload;
      state.rightEye  = rightEye  || { ...initialEye };
      state.leftEye   = leftEye   || { ...initialEye };
      state.method    = method    || 'manual';
      state.condition = condition || 'none';
      state.addPower  = addPower  || null;
      state.timestamp = Date.now();
      state.isSet     = true;
      state.extractError = null;
    },
    clearPrescription(state) {
      state.rightEye      = { ...initialEye };
      state.leftEye       = { ...initialEye };
      state.method        = null;
      state.timestamp     = null;
      state.isSet         = false;
      state.extractError  = null;
    },
    updateEye(state, action) {
      const { eye, field, value } = action.payload; // eye: 'right'|'left', field: 'sph'|'cyl'|'axis'
      if (eye === 'right') state.rightEye[field] = value;
      if (eye === 'left')  state.leftEye[field]  = value;
    },
    clearExtractError(state) {
      state.extractError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(extractPrescriptionFromImage.pending, (state) => {
        state.extractLoading = true;
        state.extractError   = null;
      })
      .addCase(extractPrescriptionFromImage.fulfilled, (state) => {
        // Don't auto-set here — let the UI show the extracted values for review first
        state.extractLoading = false;
      })
      .addCase(extractPrescriptionFromImage.rejected, (state, action) => {
        state.extractLoading = false;
        state.extractError   = action.payload;
      });
  },
});

export const {
  setPrescription,
  clearPrescription,
  updateEye,
  clearExtractError,
} = prescriptionSlice.actions;

export default prescriptionSlice.reducer;
